import React, { useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";

// Land images are huge animated files (contentAnimated ≈ 32MB WebP, raw content
// ≈ 80MB GIF). Painting one as an <img>/background freezes the main thread on
// first paint, so surfaces paint the static poster and layer one of these
// canvases on top: a worker (animatedLand.worker.ts) streams, decodes and
// draws the animation, and the canvas fades in once the first frame lands.

// Off-thread playback needs WebCodecs' ImageDecoder plus OffscreenCanvas
// (Chromium today). Anything else keeps the static poster — never fall back to
// painting the animated file on the main thread, that is the freeze this avoids.
const canPlayOffThread = () => {
  if (typeof Worker === "undefined" || !("ImageDecoder" in window)) {
    return false;
  }
  if (!("transferControlToOffscreen" in HTMLCanvasElement.prototype)) {
    return false;
  }
  // Reduced motion: an autoplaying animation is motion too — keep the still poster.
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    return false;
  }
  // Don't pull a 30MB+ file over a metered or slow connection.
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (conn?.saveData || /^(slow-2g|2g|3g)$/.test(conn?.effectiveType ?? "")) {
    return false;
  }
  return true;
};

// The blurred copy is drawn this many times smaller and stretched back up by
// CSS: blurring a tiny canvas costs a fraction as much and reads the same.
const BLUR_DOWNSCALE = 8;

export const ANIMATED_LAND_PLAYING_CLASS = "ef-anim-playing";

// Hidden until the worker has drawn the first frame, then fades in over the
// poster (same picture, same cover framing — reads as the still coming to
// life). Callers place it above the poster and below any scrim/text; override
// position/size through `style` where the layer isn't a plain inset-0 fill.
export const AnimatedLandCanvas = styled("canvas")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  opacity: 0,
  transition: "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1)",
  [`&.${ANIMATED_LAND_PLAYING_CLASS}`]: { opacity: 1 },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

export interface AnimatedLandOptions {
  // Hold off before downloading/decoding, so the work never competes with an
  // entrance animation (or the map's tile loads during a fly).
  startDelayMs?: number;
  // CSS-px blur for the optional second canvas (`blurCanvasRef`), fed from the
  // same decode — for blurred-backdrop layers that used to be a 2nd <img>.
  blurPx?: number;
  // false pauses decoding (e.g. the face is flipped away); the canvas keeps
  // its last frame.
  active?: boolean;
}

/**
 * Drives one animation onto `canvasRef` (and `blurCanvasRef` when `blurPx` is
 * set) from a worker. Render the canvases with `key={src}`: a canvas can hand
 * its control to a worker only once, so a new source needs new elements.
 */
export const useAnimatedLand = (
  src: string | null | undefined,
  { startDelayMs = 0, blurPx = 0, active = true }: AnimatedLandOptions = {}
) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const blurCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const [playing, setPlaying] = useState(false);

  const syncPaused = useCallback(() => {
    workerRef.current?.postMessage({ type: activeRef.current && !document.hidden ? "resume" : "pause" });
  }, []);

  useEffect(() => {
    setPlaying(false);
    if (!src || !canPlayOffThread()) {
      return;
    }
    const timer = window.setTimeout(() => {
      const targets: { canvas: OffscreenCanvas; blur: number }[] = [];
      // Size each backing store once: these layers are fixed-size, and after
      // the transfer only the worker could resize them.
      const addTarget = (el: HTMLCanvasElement | null, scale: number, blur: number) => {
        if (!el || !el.clientWidth || !el.clientHeight) {
          return;
        }
        el.width = Math.max(1, Math.round(el.clientWidth * scale));
        el.height = Math.max(1, Math.round(el.clientHeight * scale));
        try {
          targets.push({ canvas: el.transferControlToOffscreen(), blur });
        } catch {
          // Already transferred (canvas not re-keyed on a src change) — skip it.
        }
      };
      addTarget(canvasRef.current, Math.min(window.devicePixelRatio || 1, 2), 0);
      if (blurPx > 0) {
        addTarget(blurCanvasRef.current, 1 / BLUR_DOWNSCALE, blurPx / BLUR_DOWNSCALE);
      }
      if (!targets.length) {
        return;
      }
      const worker = new Worker(new URL("./animatedLand.worker.ts", import.meta.url));
      workerRef.current = worker;
      worker.onmessage = (e: MessageEvent<{ type: string }>) => {
        if (e.data?.type === "first-frame") {
          setPlaying(true);
        } else {
          // unsupported / error: the poster underneath simply stays.
          worker.terminate();
        }
      };
      worker.postMessage(
        { type: "start", url: src, targets },
        targets.map((t) => t.canvas)
      );
      syncPaused();
      // A hidden tab shouldn't keep decoding frames nobody sees.
      document.addEventListener("visibilitychange", syncPaused);
    }, startDelayMs);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", syncPaused);
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, [src, startDelayMs, blurPx, syncPaused]);

  useEffect(() => {
    syncPaused();
  }, [active, syncPaused]);

  return { canvasRef, blurCanvasRef, playing };
};

/** A single inset-0 animated layer. Mount with `key={src}`. */
export const AnimatedLandLayer: React.FC<{
  src?: string | null;
  startDelayMs?: number;
  active?: boolean;
  style?: React.CSSProperties;
}> = ({ src, startDelayMs, active, style }) => {
  const { canvasRef, playing } = useAnimatedLand(src, { startDelayMs, active });
  return (
    <AnimatedLandCanvas
      ref={canvasRef}
      aria-hidden="true"
      className={playing ? ANIMATED_LAND_PLAYING_CLASS : undefined}
      style={style}
    />
  );
};
